import React, { useCallback, useContext, useRef, useState } from 'react';

import CapterraPixel from 'common/ads/CapterraPixel';
import AJAX from 'common/AJAX';
import RegistrationWrapper from 'common/canny/register/RegistrationWrapper';
import { TrackEventContext } from 'common/containers/EventContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import useMutation from 'common/hooks/useMutation';
import SubdomainInputV2 from 'common/inputs/SubdomainInputV2';
import Strings from 'common/Strings';
import Field from 'common/uiV3/Field';
import Input from 'common/uiV3/Input';
import VStack from 'common/uiV3/VStack';
import UserAvatar from 'common/user/UserAvatar';
import getCookies from 'common/util/getCookies';
import validateInput from 'common/validateInput';

import type { CreateCompanyRequest, CreateCompanyResponse } from 'common/api/endpoints/companies';

const errors = {
  'email domain is not trusted':
    'The email linked to your user is not allowed to create a new company. Please, change your email to continue.',
  'subdomain taken': 'Sorry, this subdomain is already in use by someone else. Try something else.',
  'slow down':
    'You are trying to create too many accounts. Please wait a few minutes before trying again.',
  default: Strings.miscError,
};

interface CreateCompanyFormProps {
  onSuccess?: (data: { subdomain: string }) => void;
  trialPlan?: 'starter' | 'growth' | 'core' | 'pro';
}

const CreateCompanyForm: React.FC<CreateCompanyFormProps> = ({ onSuccess, trialPlan }) => {
  const viewer = useContext(ViewerContext);
  const trackEvent = useContext(TrackEventContext);

  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<CreateCompanyResponse | null>(null);

  // Purposefully not using createCompanyMutation.isLoading because we want to
  // keep the loading state until the redirect is complete.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const companyRef = useRef<HTMLInputElement>(null);
  const subdomainRef = useRef<HTMLInputElement>(null);

  const createCompany = useMutation<CreateCompanyResponse, CreateCompanyRequest>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/company/create', data);
      return response;
    },
    isResponseSuccessful: (response) => 'success' in response,
    errors,
    onSuccess: (responseObject, variables) => {
      if (!trackEvent) {
        return;
      }

      trackEvent('Registered Company');

      if (responseObject.isVIP) {
        trackEvent('Registered VIP Company');
      }

      if (responseObject.qualified) {
        trackEvent('Registered Qualified Company');
      }

      setApiResponse(responseObject);

      // so event can send
      setTimeout(() => {
        onSuccess?.({ subdomain: variables.subdomain });
      }, 400);
    },
    onError: (error: Error) => {
      trackEvent('Failed to Register', {
        error: error.message,
      });
      setIsSubmitting(false);
    },
  });

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (createCompany.isLoading) {
        return;
      }

      trackEvent('Submitted Register Form');

      const companyNameValue = companyRef.current?.value.trim() || '';
      const subdomain = subdomainRef.current?.value.trim() || '';

      let validationError: string | null = null;
      if (!companyNameValue || !validateInput.companyName(companyNameValue)) {
        validationError = 'Please enter a valid company name.';
      } else if (!subdomain || !validateInput.subdomain(subdomain)) {
        validationError = 'Please enter a valid subdomain.';
      }

      if (validationError) {
        setError(validationError);
        return;
      }

      trackEvent('Completed Registration');
      trackEvent('Made Register Request');

      setError(null);

      const [hubspotutk] = getCookies(['hubspotutk']);

      setIsSubmitting(true);

      createCompany.mutate({
        companyName: companyNameValue,
        hubspotutk,
        subdomain,
        trialPlan,
      });
    },
    [createCompany, trackEvent, trialPlan],
  );

  const renderPixel = () => {
    if (!apiResponse?.success) {
      return null;
    }

    if (!apiResponse.qualified && !apiResponse.isVIP) {
      return null;
    }

    return (
      <div className="absolute">
        <CapterraPixel />
      </div>
    );
  };

  return (
    <RegistrationWrapper className="wrapper-slot:max-w-[500px]">
      <RegistrationWrapper.Header>
        <RegistrationWrapper.Title>Create a new company</RegistrationWrapper.Title>
        <RegistrationWrapper.Description>
          Get started with Canny for&nbsp;free, no credit card&nbsp;required
        </RegistrationWrapper.Description>
      </RegistrationWrapper.Header>
      <form onSubmit={onSubmit}>
        <VStack className="gap-6">
          {viewer && (
            <VStack className="gap-3 items-center">
              <UserAvatar loading="eager" size="large" user={viewer} />
              <div>{viewer.name}</div>
            </VStack>
          )}
          <Field.Group>
            <Field>
              <Field.Label htmlFor="company-name" required>
                Company Name
              </Field.Label>
              <Field.Content>
                <Input
                  autoFocus
                  id="company-name"
                  name="company name"
                  onChange={setCompanyName}
                  size="large"
                  ref={companyRef}
                  placeholder="Company or app name"
                />
              </Field.Content>
            </Field>
            <Field>
              <Field.Label htmlFor="subdomain" required>
                Subdomain
              </Field.Label>
              <Field.Content>
                <SubdomainInputV2
                  id="subdomain"
                  value={companyName}
                  placeholder="Subdomain"
                  size="large"
                  ref={subdomainRef}
                />
              </Field.Content>
              <Field.Error>{error || createCompany.error?.message}</Field.Error>
            </Field>
          </Field.Group>
        </VStack>
        <RegistrationWrapper.Navigation
          className="mt-8"
          back={{
            visible: false,
          }}
          continue={{
            text: 'Create Company',
            endIcon: undefined,
          }}
          isLoading={isSubmitting}
        />
      </form>
      {renderPixel()}
    </RegistrationWrapper>
  );
};

export default CreateCompanyForm;
